import type { CoverageOptionDto } from '../../../../../api/submissions/submission-coverages.types';
import type { AccountDto } from '../../../../../types';
import type {
  BoostRaterVersionResponse,
  EnhancedBusinessAndCbiResponse,
  GetPrime100CoveragesResponse,
  SocialEngineeringResponse,
  TransformedEligibleCoverageValuesResponse,
} from '../api/types';
import type { formatGlobalValues } from '../transformers/globalValues';
import type { DynamicFieldAnswersPayload } from '../constants/p100-v6-dynamic-field-mapping';

/* Provides a base type for all p100 quoting pages that we can extend upon */
export interface Prime100QuotePageState<
  T = Record<Prime100Coverages, CoverageState>
> {
  account: AccountDto;
  agencyId: string;
  disableRuleEvaluation?: boolean;
  boostRaterVersion: BoostRaterVersionResponse;
  effectiveDate: string;
  endDate: string;
  socialEngineering: SocialEngineeringResponse;
  enhancedBusinessAndCbi?: EnhancedBusinessAndCbiResponse;
  coverageValues: TransformedEligibleCoverageValuesResponse;
  coverageData: GetPrime100CoveragesResponse;
  globalValues: ReturnType<typeof formatGlobalValues>;
  previousGlobalValues?: ReturnType<typeof formatGlobalValues>;
  coverageState: T;
  minEffectiveDate: Date;
  maxEffectiveDate: Date;
  additionalBrokerFee: number;
  submissionId?: string;
  additionalNamedInsured: string[];
  coverageOption?: CoverageOptionDto;
  dynamicFieldAnswers?: DynamicFieldAnswersPayload;
}

export enum Prime100Coverages {
  PUBLIC_RELATIONS_EXPENSE = 'PUBLIC_RELATIONS_EXPENSE',
  RESTORATION_OF_ELECTRONIC_DATA = 'RESTORATION_OF_ELECTRONIC_DATA',
  POST_BREACH_REMEDIATION = 'POST_BREACH_REMEDIATION',
  SOCIAL_ENGINEERING = 'SOCIAL_ENGINEERING',
  PCI_FINES_AND_PENALTIES = 'PCI_FINES_AND_PENALTIES',
  ADDITIONAL_INSURED = 'ADDITIONAL_INSURED',
  TELECOM_FRAUD = 'TELECOM_FRAUD',
  HARDWARE_REPLACEMENT_COSTS = 'HARDWARE_REPLACEMENT_COSTS',
  BI_EXTRA_EXPENSE_V5 = 'BI_EXTRA_EXPENSE_V5',
  EXTORTION_THREATS_AND_RANSOM_PAYMENT = 'EXTORTION_THREATS_AND_RANSOM_PAYMENT',
  COMPUTER_FUNDS_TRANSFER_FRAUD = 'COMPUTER_FUNDS_TRANSFER_FRAUD',
  SECURITY_BREACH_LIABILITY = 'SECURITY_BREACH_LIABILITY',
  BI_EXTRA_EXPENSE = 'BI_EXTRA_EXPENSE',
  EXTORTION_THREATS_AND_RANSOM_PAYMENT_V5 = 'EXTORTION_THREATS_AND_RANSOM_PAYMENT_V5',
  REGULATOR_DEFENSE_AND_PENALTIES = 'REGULATOR_DEFENSE_AND_PENALTIES',
  COURT_ATTENDANCE_COSTS = 'COURT_ATTENDANCE_COSTS',
  WEBSITE_MEDIA_CONTENT_LIABILITY = 'WEBSITE_MEDIA_CONTENT_LIABILITY',
  SECURITY_BREACH_EXPENSE = 'SECURITY_BREACH_EXPENSE',
  TRIA = 'TRIA',
  TRIA_EXCLUSION = 'TRIA_EXCLUSION',
  ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE = 'ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_INCLUSIVE',
  ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME = 'ENHANCED_BUSINESS_AND_CONTINGENT_BUSINESS_INCOME',
  SERVICE_FRAUD_AND_CRYPTOJACKING = 'SERVICE_FRAUD_AND_CRYPTOJACKING',
  DEDICATED_BREACH_COSTS_ENDORSEMENT = 'DEDICATED_BREACH_COSTS_ENDORSEMENT',
  EXCLUSION_OF_CONTINGENT_BUSINESS_INCOME = 'EXCLUSION_OF_CONTINGENT_BUSINESS_INCOME',
  REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT = 'REVERSE_SOCIAL_ENGINEERING_ENDORSEMENT',
  WEBSITE_MEDIA_CONTENT_LIABILITY_V6 = 'WEBSITE_MEDIA_CONTENT_LIABILITY_V6',
  FUNDS_TRANSFER_LIABILITY_LOSS_EXCLUSION = 'FUNDS_TRANSFER_LIABILITY_LOSS_EXCLUSION',
  ADDITIONAL_DEFENSE_EXPENSES_LIMIT = 'ADDITIONAL_DEFENSE_EXPENSES_LIMIT',
  THIRD_PARTY_PATENT_OR_TRADE_SECRET_INFO_CARVEBACK = 'THIRD_PARTY_PATENT_OR_TRADE_SECRET_INFO_CARVEBACK',
  WAIVER_OF_SUBROGATION = 'WAIVER_OF_SUBROGATION',
  BLANKET_ADDITIONAL_INSURED = 'BLANKET_ADDITIONAL_INSURED',
  OTHER_INSURANCE_AMENDATORY_PRIMARY_AND_NON_CONTRIBUTORY = 'OTHER_INSURANCE_AMENDATORY_PRIMARY_AND_NON_CONTRIBUTORY',
  BUSINESS_INCOME_ECOMMERCE_AMENDMENT = 'BUSINESS_INCOME_ECOMMERCE_AMENDMENT',
  CONTINGENT_BUSINESS_INCOME_SECURITY_FAILURE = 'CONTINGENT_BUSINESS_INCOME_SECURITY_FAILURE',
  CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_IT = 'CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_IT',
  CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_NON_IT = 'CONTINGENT_BUSINESS_INCOME_SYSTEM_FAILURE_NON_IT',
  FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT = 'FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CONDITION_PRECEDENT',
  FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS = 'FUNDS_TRANSFER_FRAUD_AND_SE_WITH_CP_CLIENT_HELD_FUNDS',
  DEFENSE_AND_SETTLEMENT_AMENDATORY = 'DEFENSE_AND_SETTLEMENT_AMENDATORY',
  FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING = 'FUNDS_TRANSFER_FRAUD_AND_SOCIAL_ENGINEERING',
  FUNDS_TRANSFER_LIABILITY_SUBLIMIT = 'FUNDS_TRANSFER_LIABILITY_SUBLIMIT',
  BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT = 'BUSINESS_INCOME_AND_EXTRA_EXPENSE_SUBLIMIT',
  REPUTATIONAL_HARM_SUBLIMIT = 'REPUTATIONAL_HARM_SUBLIMIT',
  INVOICE_MANIPULATION = 'INVOICE_MANIPULATION',
  ADDITIONAL_NAMED_INSURED = 'ADDITIONAL_NAMED_INSURED',
}

export enum GlobalPropertyKeys {
  retroactivePeriod = 'retroactivePeriod',
  deductible = 'deductible',
  waitingPeriod = 'waitingPeriod',
  limit = 'limit',
}

export type GlobalPropertyNames = keyof typeof GlobalPropertyKeys;

export type GlobalValues = {
  [K in GlobalPropertyKeys]: {
    value: number;
    options: number[];
  };
};

export enum Prime100CoverageGroups {
  FIRST_PARTY_COVERAGES = 'First Party Coverages',
  THIRD_PARTY_COVERAGES = 'Third Party Coverages',
  ENDORSEMENTS = 'Endorsements',
  TRIA = 'TRIA',
  ADDITIONAL_NAMED_INSURED = 'Additional Named Insured',
}

export type CoverageVersion = {
  version: BoostRaterVersionResponse;
  name: string;
  tooltip?: string;
  errorMessage?: string;
  ordinal: number;
};

export type HydratedCoverageInfo = {
  [K in Prime100CoverageGroups]: CoverageState[];
};

export type CoverageState<T = any> = {
  id: Prime100Coverages;
  isMandatory?: boolean;
  isMandatoryLocked?: boolean;
  isAvailable?: boolean;
  isSelected: boolean;
  nonStandardCoverage: boolean;
  limit: number;
  limits: number[];
  deductibles?: number[];
  deductible?: number;
  hasModal?: boolean;
  modalData: T;
  name: string;
  tooltip?: string;
  supplementalText?: string;
  subCoverages?: Prime100Coverages[];
  isSubCoverage?: boolean;
  isDisabled?: boolean;
  values?: string[];
  selectedValue?: string;
};

export type AdditionalInsureds = {
  address1: string;
  address2: string;
  businessName: string;
  city: string;
  naicsCode: string;
  naicsDescription: string;
  state: string;
  zipCode: string;
}[];

export type AdditionalNamedInsured = string[];

export enum SocialEngQuestions {
  IS_VERIFYING_BANK_ACCOUNTS = 'isVerifyingBankAccounts',
  IS_AUTHENTICATING_FUND_TRANSFERS = 'isAuthenticatingFundTransferRequests',
  IS_PREVENTING_UNAUTHORIZED_WIRE_TRANSFERS = 'isPreventingUnauthorizedWireTransfers',
  //due to really bad data. prefixing with underscores to avoid confusion
  __IS_AUTHENTICATING_FUNDS_TRANSFERS = 'isAuthenticatingFundsTransferRequest',
}

export const DF_SOCIAL_ENG_KEYS = {
  isVerifyingBankAccountsAnswer: SocialEngQuestions.IS_VERIFYING_BANK_ACCOUNTS,
  authenticateFundTransferRequests:
    SocialEngQuestions.IS_AUTHENTICATING_FUND_TRANSFERS,
  isPreventingUnauthorizedWireTransfersAnswer:
    SocialEngQuestions.IS_PREVENTING_UNAUTHORIZED_WIRE_TRANSFERS,
};
